import React, { useState } from "react";
import { motion } from "framer-motion";
import Section from "./hoc/Section";

const textVariant = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const containerVariant = {
  hidden: { opacity: 0, x: 60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.25 },
  },
};

const item = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
};


// Dynamic content based on selected button
const content = {
  vision:
    "To empower companies and individuals to embrace and excel with the latest technologies, fostering innovation, efficiency, and growth in an ever-evolving digital world.",
  goals:
    "Our goal is to be the backbone for all tech-enabled giants by providing reliable, scalable, and innovative solutions that power their growth. We strive to lead in adopting emerging technologies like AI, blockchain, and cloud to keep our clients ahead of the curve. By building a world-class team and fostering continuous learning, we ensure talent excellence and cutting-edge delivery. With security, trust, and compliance at our core, we aim to become the trusted partner enterprises depend on. Beyond services, we seek to shape ecosystems through open-source contributions, global collaborations, and technology leadership that empowers lasting impact.",
};

const About = () => {
  const [selected, setSelected] = useState("vision"); // default is Vision

  return (
    <section className="w-full min-h-screen bg-background flex justify-center px-4 sm:px-10 text-white">
      <div className="flex flex-col gap-16 max-w-[1200px] w-full">
        {/* ---------- Heading ---------- */}
        <header className="text-center space-y-9">
          <motion.div
            variants={textVariant}
            initial="hidden"
            whileInView="show"
            viewport={{ amount: 0.3 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold">
              About Us
            </h1>
            <p className="text-sm sm:text-base text-gray-400 text-wrap max-w-8xl justify-start mx-auto mt-4">
              We are a diverse team of skilled professionals with proven expertise across domains such as Information Technology, 
              Blockchain, and Insurance. Our collective experience has empowered us to successfully deploy 500+ smart contracts while 
              delivering secure and scalable solutions. Beyond blockchain, we extend our capabilities into AI, design, and marketing, 
              offering end-to-end support that helps businesses innovate and grow. At our core, we are problem-solvers who combine 
              technical excellence with creativity to transform ideas into impactful, future-ready solutions.
            </p>
          </motion.div>
        </header>

        {/* ---------- Image Row ---------- */}
        <motion.section
          variants={containerVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ amount: 0.3 }}
          className="flex flex-col md:flex-row justify-center items-center gap-6 w-full"
        >
          <img
            src="https://cdn.prod.website-files.com/67d9b8d389a265b16840fde9/67d9b8d389a265b16840ff05_about-hero-image-1.webp"
            alt="team working"
            className="w-full md:w-[500px] rounded-xl object-cover"
          />
          <img
            src="https://cdn.prod.website-files.com/67d9b8d389a265b16840fde9/67d9b8d389a265b16840ff06_about-hero-image-2.webp"
            alt="office discussion"
            className="w-full md:w-[700px] rounded-xl object-cover"
          />
        </motion.section>

        {/* ---------- Feature Cards ---------- */}
        <section className="grid grid-cols-1 sm:grid-cols-3 place-items-center w-full gap-y-10 sm:gap-x-6">
          {[
  {
    title: "Innovation-Driven",
    desc: "Reliable and scalable solutions with cutting-edge technology, global reach, trusted security, and talent excellence for tech-enabled giants worldwide.",
    icon: "https://cdn.prod.website-files.com/67d9b8d389a265b16840fde9/67d9b8d389a265b16840ff08_about-hero-info-icon-%20(3).svg",
  },
  {
    title: "Global Edge",
    desc: "With reach to the best minds worldwide, we bring top talent tailored to each project’s needs, powered by our fully remote team.",
    icon: "https://cdn.prod.website-files.com/67d9b8d389a265b16840fde9/67d9b8d389a265b16840ff09_about-hero-info-icon-%20(2).svg",
  },
  {
    title: "Growth-Oriented",
    desc: "We are more than development partners — we align with your product vision, embracing a true growth mindset to drive lasting success.",
    icon: "https://cdn.prod.website-files.com/67d9b8d389a265b16840fde9/67d9b8d389a265b16840ff07_about-hero-info-icon-%20(1).svg",
  },
].map(({ title, desc, icon }, i) => (
  <div key={i} className="gradient-border w-[380px] rounded-[50px]">
    <div className="rounded-[46px] bg-[#0a0a23] min-h-[120px] flex flex-col justify-center px-6 py-6">
      <div className="flex items-start gap-3 mb-3">
        {/* Icon */}
        <span className="w-12 h-12 flex-shrink-0">
          <img src={icon} alt={`${title} icon`} className="w-12 h-12" />
        </span>

        {/* Title + Description */}
        <div className="flex flex-col">
          <h3 className="text-lg font-bold">{title}</h3>
          <p className="text-sm text-gray-300 mt-1">{desc}</p>
        </div>
      </div>
    </div>
  </div>
))}
        </section>

       {/* ---------- Mission / Vision ---------- */}
<motion.section
      className="flex flex-col md:flex-row justify-center items-start gap-10 my-10"
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.2 }} // triggers every time on scroll
    >
      {/* ---------- Left Section with Buttons ---------- */}
      <motion.div className="flex-1 space-y-6" variants={item}>
        <h2 className="text-4xl lg:text-5xl font-bold leading-snug">
          We Focus On{" "}
          <span className="font-semibold">
            Innovation
            <br />
            And Sustainability
          </span>
        </h2>

        {/* Buttons with stagger effect */}
        <motion.div
          className="flex flex-wrap gap-4"
          variants={container}
          initial="hidden"
          whileInView="show"
        >
          <motion.button
            onClick={() => setSelected("vision")}
            className={`px-6 py-2 rounded-full transition ${
              selected === "vision"
                ? "bg-purple-600 text-white"
                : "border border-purple-500 hover:bg-purple-600 hover:text-white"
            }`}
            variants={item}
          >
            Our Vision
          </motion.button>

          <motion.button
            onClick={() => setSelected("goals")}
            className={`px-6 py-2 rounded-full transition ${
              selected === "goals"
                ? "bg-purple-600 text-white"
                : "border border-purple-500 hover:bg-purple-600 hover:text-white"
            }`}
            variants={item}
          >
            Our Goals
          </motion.button>
        </motion.div>
      </motion.div>

      {/* ---------- Right Section with Dynamic Text ---------- */}
      <motion.div
        className="flex-1 text-gray-300 space-y-4 leading-relaxed"
        style={{ minHeight: "220px" }}
        key={selected} // triggers animation on change
        variants={textVariant}
        initial="hidden"
        animate="show"
      >
        <motion.p variants={textVariant}>{content[selected]}</motion.p>
      </motion.div>
    </motion.section>
      </div>
    </section>
  );
};

export default Section(About, "about");
